import type { IFieldEdit } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Box, Divider, Drawer, Group, Loader, Stack, Text } from "@mantine/core"
import { useMemo } from "react"
import { formatDateTime } from "../../../lib/format-date"
import { computeWordDiff } from "../diff"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for FieldEditHistory. Renders the drawer with the field path,
 * loading state, empty state, or the chronological list of edits with diffs.
 * The smart wrapper at ../index.tsx owns data fetching.
 */
export function FieldEditHistoryView({ fieldPath, opened, onClose, edits, isLoading }: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={i18n._(msg`Edit History`)}
      position="right"
      size="md"
      overlayProps={{ opacity: 0.3 }}
    >
      <Text size="xs" c="dimmed" mb="md" className={classes.fieldPath}>
        <Trans>
          Field: <code>{fieldPath}</code>
        </Trans>
      </Text>

      {isLoading && (
        <Box ta="center" py="xl">
          <Loader size="sm" />
        </Box>
      )}

      {!isLoading && edits.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          <Trans>No edit history for this field.</Trans>
        </Text>
      )}

      {!isLoading && edits.length > 0 && (
        <Stack gap="md">
          {edits.map((edit, i) => (
            <VersionEntry key={edit.version} edit={edit} prevEdit={i > 0 ? edits[i - 1] : null} />
          ))}
        </Stack>
      )}
    </Drawer>
  )
}

function VersionEntry({ edit, prevEdit }: { edit: IFieldEdit; prevEdit: IFieldEdit | null }): React.JSX.Element {
  const { i18n } = useLingui()
  const isOriginal = edit.version === 0
  const diff = useMemo(
    () => (prevEdit && !isOriginal ? computeWordDiff(prevEdit.value, edit.value) : null),
    [prevEdit, isOriginal, edit.value],
  )

  const dateStr = useMemo(() => formatDateTime(i18n, edit.editedAt) || edit.editedAt, [i18n, edit.editedAt])

  return (
    <Box>
      <Group gap="xs" mb="xs">
        <Badge color={isOriginal ? "gray" : "info"} variant="light" size="sm">
          {isOriginal ? i18n._(msg`Original (AI)`) : i18n._(msg`Edit ${edit.version}`)}
        </Badge>
        {!isOriginal && edit.editedByName && (
          <Text size="xs" fw={500}>
            {edit.editedByName}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {dateStr}
        </Text>
      </Group>

      {diff ? (
        <Text size="sm" className={classes.diff}>
          {diff.map((part, i) => {
            // Position is the identity: the same (type, text) pair can repeat
            // across a diff (e.g. several unchanged words), so keying on content
            // alone collides. Index is stable for a given rendered diff.
            const k = `${i}:${part.type}`
            return part.type === "equal" ? (
              <span key={k}>{part.text}</span>
            ) : part.type === "added" ? (
              <mark key={k} className={classes.added}>
                {part.text}
              </mark>
            ) : (
              <s key={k} className={classes.removed}>
                {part.text}
              </s>
            )
          })}
        </Text>
      ) : (
        <Text size="sm" className={classes.diff}>
          {edit.value}
        </Text>
      )}

      <Divider mt="md" />
    </Box>
  )
}
